import '.eo{column-gap:1rem;}.ssr-plugin'
import { useEffect, useMemo, useState } from 'react'
import { useAuthState, useClient } from '@based/sdk/react'
import { useSearchParams } from 'wouter'
import { styled } from 'inlines'
import Row from '@app/components/layout/Row'
import Column from '@app/components/layout/Column'
import {
  AppearSection,
  AppearItem,
} from '@app/components/blocks/contentSection/blocks/AppearInViewport'
import { PageLayout } from '@app/pages/layout/PageLayout'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { Button } from '@app/components/core/button/Button'
import { Modal } from '@app/components/blocks/modal/Modal'
import { DiscussionCard } from '@app/components/blocks/discussions/DiscussionCard'
import { PostDetailModal } from '@app/components/blocks/discussions/PostDetailModal'
import { CreatePostDrawer } from '@app/components/blocks/discussions/CreatePostDrawer'
import {
  DiscussionFilters,
  DiscussionFiltersValue,
} from '@app/components/blocks/discussions/DiscussionFilters'
import { Empty } from '@app/components/blocks/empty/Empty'
import { useTranslations } from '@i18n/useLanguage'
import useDateFormat, { useRelativeDateFormat } from '@app/util/useDateFormat'
import useMediaQuery from '@app/util/useMediaQuery'
import { fullName } from '@app/util/helpers'
import {
  GlobalPostViewModel,
} from 'functions/app/thread/view-global'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { getPostedInLabel, getPostShareTitle, getPostShareUrl } from './discussionsHelpers'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { usePostActions } from '@app/util/hooks/usePostActions'

export const DiscussionsPage = () => {
  const t = useTranslations().app.discussions
  const dateFormat = useDateFormat()
  const relativeDate = useRelativeDateFormat()
  const isMobile = useMediaQuery()
  const authState = useAuthState()
  const client = useClient()
  const { withAuthCheck } = useAuthModal()
  const { deletePost } = usePostActions()
  const [searchParams, setSearchParams] = useSearchParams()

  const [filters, setFilters] = useState<DiscussionFiltersValue>(() => {
    const category = searchParams.get('category')
    const region = searchParams.get('region')
    return {
      categoryFilter:
        category === 'my-posts'
          ? 'my-posts'
          : category && !isNaN(Number(category))
            ? Number(category)
            : 'all',
      regionFilter: region ?? null,
    }
  })
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [selectedPost, setSelectedPost] = useState<GlobalPostViewModel | null>(
    null,
  )
  const [editPost, setEditPost] = useState<GlobalPostViewModel | null>(null)
  const [confirmDeletePostId, setConfirmDeletePostId] = useState<number | null>(
    null,
  )
  const [deleteLoading, setDeleteLoading] = useState(false)

  const queryPayload = useMemo(() => {
    const payload: {
      categoryId?: number
      authorId?: number
      regionId?: number
      limit?: number
    } = { limit: 100 }

    if (filters.categoryFilter === 'my-posts' && authState.userId) {
      payload.authorId = Number(authState.userId)
    } else if (typeof filters.categoryFilter === 'number') {
      payload.categoryId = filters.categoryFilter
    }

    if (filters.regionFilter) {
      payload.regionId = Number(filters.regionFilter)
    }

    return payload
  }, [authState.userId, filters])

  const [posts, setPosts] = useState<GlobalPostViewModel[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    const close = client
      .query('thread-view-global', queryPayload)
      .subscribe((result) => {
        setPosts(Array.isArray(result) ? (result as GlobalPostViewModel[]) : [])
        setLoading(false)
      })
    return () => close()
  }, [client, queryPayload])

  // Open the post modal automatically when arriving via a shared link
  // (e.g. /prikbord?post=123).
  useEffect(() => {
    const postParam = searchParams.get('post')
    if (!postParam) {
      return
    }
    const targetId = Number(postParam)
    if (!targetId) {
      return
    }
    const found = posts.find((post) => post.id === targetId)
    if (found) {
      setSelectedPost(found)
    }
  }, [searchParams, posts])

  const handlePostClick = (post: GlobalPostViewModel) => {
    setSelectedPost(post)
  }

  const handleDetailClose = () => {
    setSelectedPost(null)
    if (searchParams.has('post')) {
      const next = new URLSearchParams(searchParams)
      next.delete('post')
      setSearchParams(next)
    }
  }

  const handleFiltersChange = (next: DiscussionFiltersValue) => {
    setFilters(next)
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev.toString())
      if (next.categoryFilter === 'all') {
        params.delete('category')
      } else {
        params.set('category', String(next.categoryFilter))
      }
      if (next.regionFilter === null) {
        params.delete('region')
      } else {
        params.set('region', next.regionFilter)
      }
      return params
    })
  }

  const isAuthor = (post: GlobalPostViewModel) =>
    !!authState.userId && post.author?.id === Number(authState.userId)

  const handleDelete = async () => {
    if (confirmDeletePostId === null) {
      return
    }
    setDeleteLoading(true)
    const ok = await deletePost(confirmDeletePostId)
    if (ok) {
      setPosts((prev) => prev.filter((post) => post.id !== confirmDeletePostId))
      if (selectedPost?.id === confirmDeletePostId) {
        setSelectedPost(null)
      }
      setConfirmDeletePostId(null)
    }
    setDeleteLoading(false)
  }

  const handleLike = (postId: number) => {
    void withAuthCheck(async () => {
      const userId = authState.userId ? Number(authState.userId) : null
      if (userId) {
        // Optimistically toggle the like locally: the global collection query
        // subscription does not re-emit on a nested viewers edge change.
        setPosts((prev) =>
          prev.map((post) => {
            if (post.id !== postId) {
              return post
            }
            const viewers = post.viewers ?? []
            const alreadyLiked = viewers.some(
              (viewer) =>
                viewer.id === userId && viewer.$reaction === 'like',
            )
            return {
              ...post,
              viewers: alreadyLiked
                ? viewers.filter(
                    (viewer) =>
                      !(viewer.id === userId && viewer.$reaction === 'like'),
                  )
                : [...viewers, { id: userId, $reaction: 'like' }],
            }
          }),
        )
      }
      await client.call('react-post', { postId })
    })
  }

  const hasActiveFilters =
    filters.categoryFilter !== 'all' || filters.regionFilter !== null

  const showFilters = hasActiveFilters || (!loading && posts.length > 0)

  const selectedAuthorName = selectedPost?.author
    ? fullName({
        firstName: selectedPost.author.firstName,
        lastName: selectedPost.author.lastName,
        middlePart: selectedPost.author.middlePart,
      })
    : ''

  return (
    <PageLayout gap="2rem">
      <Row
        style={{
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <Column gap="0.5rem" alignItems="stretch">
          <Title.H2 style={{ margin: 0 }}>{t.title}</Title.H2>
          <Text.Medium>{t.subTitle}</Text.Medium>
        </Column>
        <Button
          variant="primary"
          label={t.createButton}
          icon={faPlus}
          iconPosition="left"
          onClick={() => setIsCreateOpen(true)}
        />
      </Row>

      {showFilters ? (
        <DiscussionFilters
          showCategories
          showLocationFilters
          value={filters}
          onFiltersChange={handleFiltersChange}
        />
      ) : null}

      {!loading && (
        <Text.Small style={{ color: 'var(--gray-300)', marginBottom: '1rem' }}>
          {t.postCount.replace('{0}', String(posts.length))}
        </Text.Small>
      )}

      {loading ? (
        <Text.Medium style={{ color: 'var(--gray-300)' }}>{t.loading}</Text.Medium>
      ) : posts.length === 0 ? (
        <Empty title={t.emptyTitle} style={{ marginTop: '2rem' }}>
          {t.emptyDescription}
        </Empty>
      ) : (
        <AppearSection>
          <div className="eo"
            style={{
              columnCount: isMobile ? 1 : 3,}}
          >
            {posts.map((post, index) => {
              const likedUserIds = (post.viewers ?? [])
                .filter((viewer) => viewer.$reaction === 'like')
                .map((viewer) => viewer.id.toString())
              const liked =
                !!authState.userId &&
                likedUserIds.includes(authState.userId.toString())

              return (
                <AppearItem
                  key={post.id}
                  staggerIndex={index % (isMobile ? 1 : 3)}
                  style={{ breakInside: 'avoid', marginBottom: '1rem' }}
                >
                  <DiscussionCard
                  authorName={
                    post.author
                      ? fullName({
                          firstName: post.author.firstName,
                          lastName: post.author.lastName,
                          middlePart: post.author.middlePart,
                        })
                      : ''
                  }
                  authorAvatar={post.author?.avatar?.src}
                  postedIn={getPostedInLabel(post.threads)}
                  date={
                    post.createdAt
                      ? relativeDate(new Date(post.createdAt))
                      : ''
                  }
                  updatedAt={
                    post.updatedAt && post.updatedAt > (post.createdAt ?? 0)
                      ? relativeDate(new Date(post.updatedAt))
                      : undefined
                  }
                  title={post.title ?? undefined}
                  content={post.content ?? ''}
                  likes={likedUserIds.length}
                  liked={liked}
                  onLikeClick={() => handleLike(post.id)}
                  replies={(post.replies ?? []).reduce(
                    (total, r) => total + 1 + (r.replies?.count ?? 0),
                    0,
                  )}
                  categories={post.categories}
                  media={(post.media ?? [])
                    .filter((item) => item.status === 'ready')
                    .map((item) => ({
                      src: item.src,
                      mimeType: item.mimeType,
                      name: item.name,
                    }))}
                  onClick={() => handlePostClick(post)}
                  onEdit={
                    isAuthor(post) ? () => setEditPost(post) : undefined
                  }
                  onDelete={
                    isAuthor(post)
                      ? () => setConfirmDeletePostId(post.id)
                      : undefined
                  }
                  shareUrl={getPostShareUrl(post.id)}
                  shareTitle={getPostShareTitle(
                    post.title ?? undefined,
                    post.content ?? undefined,
                  )}
                  />
                </AppearItem>
              )
            })}
          </div>
        </AppearSection>
      )}

      <CreatePostDrawer
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
      />

      <CreatePostDrawer
        open={!!editPost}
        editPost={editPost}
        onClose={() => setEditPost(null)}
      />

      <PostDetailModal
        postId={selectedPost?.id ?? null}
        threadId={selectedPost?.threads?.[0]?.id ?? null}
        authorName={selectedAuthorName}
        postedIn={selectedPost ? getPostedInLabel(selectedPost.threads) : undefined}
        date={
          selectedPost?.createdAt
            ? dateFormat(new Date(selectedPost.createdAt))
            : ''
        }
        title={selectedPost?.title ?? undefined}
        categories={selectedPost?.categories}
        onClose={handleDetailClose}
        onEdit={
          selectedPost && isAuthor(selectedPost)
            ? () => { setEditPost(selectedPost); setSelectedPost(null) }
            : undefined
        }
        onDelete={
          selectedPost && isAuthor(selectedPost)
            ? () => { setConfirmDeletePostId(selectedPost.id); setSelectedPost(null) }
            : undefined
        }
      />

      <Modal
        isOpen={confirmDeletePostId !== null}
        loading={deleteLoading}
        backLabel={t.cancel}
        confirmLabel={t.deleteConfirm}
        onClose={() => setConfirmDeletePostId(null)}
        onConfirm={() => void handleDelete()}
      >
        <Text.Medium>{t.deleteConfirmDescription}</Text.Medium>
      </Modal>
    </PageLayout>
  )
}
